import {
  Badge,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  PaginationComponent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@folio/components';
import {
  ClaimStatusBadge,
  claimTypeLabel,
} from '@folio/components/module-elements/ClaimStatusBadge';
import { ErrorRow } from '@folio/components/module-elements/ErrorRow';
import { LoadingRow } from '@folio/components/module-elements/LoadingRow';
import type { Tone } from '@folio/components/ui/tone';
import { formatDate } from '@folio/lib/date-formatting';
import { useQuery } from '@tanstack/react-query';
import {
  type ClaimFraudSummaryDto,
  type ClaimManagementStateDto,
  claimManagementControllerListQueryOptions,
} from 'korint-sdk';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

const PAGE_SIZE = 10;

const EMPTY_VALUE = '—';

// Party fields follow the `<section>.<attribute>` keys every claims integration emits.
const INSURED_NAME_KEYS = ['assure.firstName', 'assure.lastName'];

const insuredName = ({ fields }: ClaimManagementStateDto) =>
  INSURED_NAME_KEYS.map((nameKey): unknown => fields.find(({ key }) => key === nameKey)?.value)
    .filter((value): value is string => typeof value === 'string' && value.length > 0)
    .join(' ');

const toDate = (iso: string | undefined, locale: string) =>
  iso ? formatDate(new Date(iso), locale) : EMPTY_VALUE;

const FRAUD_TONE: Record<ClaimFraudSummaryDto['globalStatus'], Tone> = {
  clean: 'success',
  review_needed: 'warning',
  fraud: 'destructive',
  pending: 'info',
  unchecked: 'muted',
  error: 'muted',
};

function FraudBadge({ summary }: { summary?: ClaimFraudSummaryDto | null }) {
  const { t } = useTranslation();

  if (!summary) {
    return (
      <Badge variant="light" tone="muted">
        {t('claimsList.fraud.notScored')}
      </Badge>
    );
  }

  return (
    <Badge variant="light" tone={FRAUD_TONE[summary.globalStatus]}>
      {t(`claimsList.fraud.${summary.globalStatus}`)}
      {typeof summary.globalScore === 'number' && ` · ${summary.globalScore}`}
    </Badge>
  );
}

export function ClaimsListModule() {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();

  const page = Number.parseInt(searchParams.get('page') ?? '', 10);
  const currentPage = Number.isNaN(page) || page < 1 ? 1 : page;

  const {
    data: claims,
    isLoading,
    isError,
  } = useQuery({
    ...claimManagementControllerListQueryOptions({
      limit: PAGE_SIZE,
      offset: (currentPage - 1) * PAGE_SIZE,
    }),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('claimsList.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('claimsList.columns.number')}</TableHead>
              <TableHead>{t('claimsList.columns.declaredAt')}</TableHead>
              <TableHead>{t('claimsList.columns.occurredAt')}</TableHead>
              <TableHead>{t('claimsList.columns.type')}</TableHead>
              <TableHead>{t('claimsList.columns.client')}</TableHead>
              <TableHead>{t('claimsList.columns.status')}</TableHead>
              <TableHead>{t('claimsList.columns.fraud')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: PAGE_SIZE }).map((_, index) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: fixed-length skeleton placeholder
                <LoadingRow key={index} />
              ))
            ) : isError ? (
              <ErrorRow>{t('claimsList.error')}</ErrorRow>
            ) : !claims?.data.length ? (
              <ErrorRow>{t('claimsList.empty')}</ErrorRow>
            ) : (
              claims.data.map(claim => (
                <TableRow key={claim.id}>
                  <TableCell>
                    <Link to={`/claims/${claim.id}`} className="font-medium hover:underline">
                      {claim.id}
                    </Link>
                  </TableCell>
                  <TableCell>{toDate(claim.submittedAt, i18n.language)}</TableCell>
                  <TableCell>{toDate(claim.incident.occurredAt, i18n.language)}</TableCell>
                  <TableCell>{claimTypeLabel(claim)}</TableCell>
                  <TableCell>{insuredName(claim) || EMPTY_VALUE}</TableCell>
                  <TableCell>
                    <ClaimStatusBadge claim={claim} />
                  </TableCell>
                  <TableCell>
                    <FraudBadge summary={claim.fraudSummary} />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
      <CardFooter>
        <PaginationComponent
          totalData={claims?.total ?? 0}
          PAGE_SIZE={PAGE_SIZE}
          isLoading={isLoading}
        />
      </CardFooter>
    </Card>
  );
}
